import { useState, createContext, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';

import { useSocket } from './SocketContext';

type PlayerContextType = {
  username: string;
  ready: boolean;
  playing: boolean;
  toggleReady: () => void;
  quantityOfSolvedQuestions: number;
  submitAnswer: (answer: string) => void;
  timeSpent?: number;
};

type ReadyChangedDto = {
  status: Status;
};

export const PlayerContext = createContext<PlayerContextType | null>(null);

const PlayerProvider = ({ children }: any) => {
  const socket = useSocket();

  const [username, setUsername] = useState<string>('username-example');

  const [ready, setReady] = useState<boolean>(false);
  const [playing, setPlaying] = useState<boolean>(false);
  const [quantityOfSolvedQuestions, setQuantityOfSolvedQuestions] =
    useState<number>(0);

  const [timeSpent, setTimeSpent] = useState<number | undefined>();

  const toggleReady = () => {
    const newReady = !ready;

    setReady(newReady);

    socket.emit('toggleReady', (readyChangedDto: ReadyChangedDto) => {
      if (readyChangedDto.status !== 'ok') {
        toast.error('There was an error when changing your status to ready');
        setReady(!newReady);
      }
    });
  };

  const submitAnswer = (answer: string) => {
    socket.emit(
      'submitAnswer',
      {
        quantityOfSolvedQuestions,
        answer,
      },
      (isCorrect: boolean) => {
        if (isCorrect) {
          toast.success('Acertou!');

          setQuantityOfSolvedQuestions((state) => state + 1);
        } else {
          toast.error('Errou!');
        }
      },
    );
  };

  useEffect(() => {
    socket.on('playerChanged', (player: Player) => {
      if (player.id === socket.id) {
        setReady(player.ready);
        setPlaying(player.playing);
        setQuantityOfSolvedQuestions(player.quantityOfSolvedQuestions);
        setTimeSpent(player.timeSpent);
      }
    });
  }, []);

  return (
    <PlayerContext.Provider
      value={{
        username,
        ready,
        toggleReady,
        quantityOfSolvedQuestions,
        submitAnswer,
        playing,
        timeSpent,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const context = useContext(PlayerContext);

  if (!context) throw new Error('useUser must be used within a PlayerProvider');

  return context;
};

export default PlayerProvider;
